import { useState, useRef, useCallback, type MouseEvent, type TouchEvent } from "react";
import { cn } from "../lib/utils";

interface RangeSliderProps {
  value?: [number, number];
  defaultValue?: [number, number];
  onChange?: (value: [number, number]) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  formatValue?: (value: number) => string;
  className?: string;
}

export function RangeSlider({
  value,
  defaultValue = [25, 75],
  onChange,
  min = 0,
  max = 100,
  step = 1,
  disabled = false,
  formatValue = (v) => String(v),
  className,
}: RangeSliderProps) {
  const [internal, setInternal] = useState<[number, number]>(defaultValue);
  const isControlled = value !== undefined;
  const [lo, hi] = isControlled ? value! : internal;

  const trackRef = useRef<HTMLDivElement>(null);
  const dragging = useRef<"lo" | "hi" | null>(null);

  const clamp = (v: number) => Math.max(min, Math.min(max, Math.round(v / step) * step));

  const getValueFromX = useCallback(
    (clientX: number): number => {
      const track = trackRef.current;
      if (!track) return 0;
      const rect = track.getBoundingClientRect();
      const ratio = (clientX - rect.left) / rect.width;
      return clamp(min + ratio * (max - min));
    },
    [min, max, step],
  );

  const handleMove = useCallback(
    (clientX: number) => {
      if (!dragging.current || disabled) return;
      const newVal = getValueFromX(clientX);
      const next: [number, number] =
        dragging.current === "lo"
          ? [Math.min(newVal, hi), hi]
          : [lo, Math.max(newVal, lo)];
      if (!isControlled) setInternal(next);
      onChange?.(next);
    },
    [lo, hi, disabled, isControlled, getValueFromX, onChange],
  );

  const startDrag = (handle: "lo" | "hi") => (e: MouseEvent | TouchEvent) => {
    if (disabled) return;
    dragging.current = handle;

    const onMove = (ev: globalThis.MouseEvent | globalThis.TouchEvent) => {
      const clientX =
        "touches" in ev
          ? ev.touches[0].clientX
          : (ev as globalThis.MouseEvent).clientX;
      handleMove(clientX);
    };
    const onUp = () => {
      dragging.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("touchend", onUp);
    e.preventDefault();
  };

  const loPct = ((lo - min) / (max - min)) * 100;
  const hiPct = ((hi - min) / (max - min)) * 100;

  return (
    <div className={cn("range-slider", disabled && "range-slider-disabled", className)}>
      <div className="range-slider-labels">
        <span>{formatValue(lo)}</span>
        <span>{formatValue(hi)}</span>
      </div>
      <div className="range-slider-track" ref={trackRef}>
        <div
          className="range-slider-fill"
          style={{ left: `${loPct}%`, width: `${hiPct - loPct}%` }}
        />
        <button
          className="range-slider-thumb"
          style={{ left: `${loPct}%` }}
          onMouseDown={startDrag("lo")}
          onTouchStart={startDrag("lo")}
          aria-valuemin={min}
          aria-valuemax={hi}
          aria-valuenow={lo}
          role="slider"
          type="button"
        />
        <button
          className="range-slider-thumb"
          style={{ left: `${hiPct}%` }}
          onMouseDown={startDrag("hi")}
          onTouchStart={startDrag("hi")}
          aria-valuemin={lo}
          aria-valuemax={max}
          aria-valuenow={hi}
          role="slider"
          type="button"
        />
      </div>
    </div>
  );
}
